import { useCallback, useEffect, useRef, useState } from 'react'
import { NavigateFunction, useLocation } from 'react-router-dom'

import {
    ageForbidden,
    bettingWarning,
    hospitalLogo,
    serbiaLogo
} from '@/assets/icons'
import {
    ageVerificationCameraAdjuster,
    ageVerificationPoster,
    ageVerificationWarning
} from '@/assets/images'
import Container from '@/components/atoms/container/container'
import PrimaryButton from '@/components/atoms/primaryButton/primaryButton'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import { useCheckInternetConnection } from '@/hooks'
import { useSessionRecording } from '@/hooks/useSessionRecording'
import { useTranslate } from '@/i18n/useTranslate'
import { apiService } from '@/services/apiService'
import {
    getAgeVerificationDebugEnabled,
    getAgeVerificationEnabled,
    getRecordDeviceSession
} from '@/services/deviceConfigurationService'
import {
    type AgeVerificationOutcome,
    getAgeVerificationOutcome
} from '@/services/icuVerificationService'
import {
    getLocalIcuMediaStream,
    isLocalIcuMediaStreamUrl
} from '@/utilities/localIcuCameraStream'

type ActionType = 'status' | 'activate' | 'deactivate' | 'detect' | null
const ICU_CAMERA_BASE_URL = 'http://localhost:3001'

// The ICU camera service returns relative stream URLs (e.g. `/api/v1/cam/stream`);
// resolve them against the camera host so the MJPEG feed can be rendered.
// Local simulator returns `local-media-stream:{id}` tokens backed by a
// MediaStream registry on the main window.
function toAbsoluteIcuStreamUrl(streamUrl: string): string {
    if (
        isLocalIcuMediaStreamUrl(streamUrl) ||
        streamUrl.startsWith('blob:') ||
        streamUrl.startsWith('http://') ||
        streamUrl.startsWith('https://')
    ) {
        return streamUrl
    }
    return `${ICU_CAMERA_BASE_URL}${streamUrl.startsWith('/') ? streamUrl : `/${streamUrl}`}`
}

// Cache-bust HTTP MJPEG URLs so each activate opens a fresh browser connection
// instead of reusing a stale multipart response / last frame.
function withIcuStreamCacheBust(streamUrl: string): string {
    if (
        isLocalIcuMediaStreamUrl(streamUrl) ||
        streamUrl.startsWith('blob:')
    ) {
        return streamUrl
    }
    const separator = streamUrl.includes('?') ? '&' : '?'
    return `${streamUrl}${separator}t=${Date.now()}`
}

// Brief pause after clearing the feed consumer so the browser can abort the
// MJPEG request before we ask the camera service to deactivate.
const ICU_STREAM_RELEASE_SETTLE_MS = 50

// Delay (ms) before the result screens auto-advance to their destination.
const AGE_RESULT_REDIRECT_MS = 2000

const wait = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms))

function IcuCameraStreamFeed({
    streamUrl,
    className = 'age-verification-scan__feed'
}: {
    streamUrl: string
    className?: string
}) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const blobVideoRef = useRef<HTMLVideoElement>(null)
    const imgRef = useRef<HTMLImageElement>(null)
    const localStream = getLocalIcuMediaStream(streamUrl)

    useEffect(() => {
        const video = videoRef.current
        if (!video || !localStream) {
            return
        }

        video.srcObject = localStream
        void video.play()

        return () => {
            if (video.srcObject === localStream) {
                video.srcObject = null
            }
        }
    }, [localStream, streamUrl])

    // Explicitly abort the MJPEG/blob consumer on unmount. Removing the DOM
    // node alone is not always enough for browsers to drop a multipart stream,
    // and the ICU camera has a hard limit on concurrent streams.
    useEffect(() => {
        const img = imgRef.current
        const blobVideo = blobVideoRef.current

        return () => {
            if (img) {
                img.src = ''
                img.removeAttribute('src')
            }
            if (blobVideo) {
                blobVideo.removeAttribute('src')
                blobVideo.load()
            }
        }
    }, [streamUrl])

    if (localStream) {
        return (
            <video
                ref={videoRef}
                className={className}
                autoPlay
                muted
                playsInline
            />
        )
    }

    if (streamUrl.startsWith('blob:')) {
        return (
            <video
                ref={blobVideoRef}
                className={className}
                src={streamUrl}
                autoPlay
                muted
                playsInline
            />
        )
    }

    return <img ref={imgRef} className={className} src={streamUrl} alt="" />
}

export default function AgeDisclaimerTemplate({
    navigate
}: {
    navigate: NavigateFunction
}) {
    const location = useLocation()
    const nextRoute =
        (location.state as { nextRoute?: string } | undefined)?.nextRoute ??
        '/betting-flow-selection'
    const { deviceConfiguration } = useCheckInternetConnection()
    const ageVerificationEnabled = getAgeVerificationEnabled(deviceConfiguration)
    const ageVerificationDebugEnabled =
        getAgeVerificationDebugEnabled(deviceConfiguration)
    const recordingEnabled =
        ageVerificationEnabled && getRecordDeviceSession(deviceConfiguration)

    // Phase one records only this route, from its first ICU screen through
    // departure from success, fail, or manual fallback (including early exits).
    useSessionRecording(recordingEnabled)

    // The /disclaimer route dispatches between distinct experiences based on
    // device configuration:
    //  - Legacy static disclaimer when camera verification is disabled.
    //  - ICU camera age verification (production or debug UI) when enabled.
    // Each branch is its own component so hook usage stays consistent.
    if (!ageVerificationEnabled) {
        return (
            <StaticAgeDisclaimer
                navigate={navigate}
                nextRoute={nextRoute}
            />
        )
    }

    if (ageVerificationDebugEnabled) {
        return (
            <IcuCameraAgeVerificationDebug
                navigate={navigate}
                nextRoute={nextRoute}
            />
        )
    }

    return (
        <IcuCameraAgeVerification
            navigate={navigate}
            nextRoute={nextRoute}
            recordingEnabled={recordingEnabled}
        />
    )
}

// Shared legal / responsible-play disclaimer content (18+ warning poster,
// betting warnings, hospital details and institutional logos). Reused by both
// the legacy static disclaimer and the production camera age-verification
// screen so the legal copy stays in a single place.
function AgeDisclaimerLegalContent({
    showFinalWarning = true
}: {
    showFinalWarning?: boolean
}) {
    const { t } = useTranslate()

    return (
        <>
            <div className="warning">
                <img
                    className="warning__symbol"
                    src={ageForbidden}
                    alt=""
                    width={220}
                    height={220}
                />
                <p>{t('ageDisclaimer.warning')}</p>
            </div>
            <div className="betting-warning">
                <img src={bettingWarning} width={95} height={95} />
                <ul>
                    <li>{t('ageDisclaimer.bettingWarning1')}</li>
                    <li>{t('ageDisclaimer.bettingWarning2')}</li>
                    <li>{t('ageDisclaimer.bettingWarning3')}</li>
                </ul>
            </div>
            <div className="logos">
                <img src={hospitalLogo} height={50} width={50} />
                <img src={serbiaLogo} height={50} width={50} />
            </div>
            <div className="hospital-info">
                <p className="uppercase">{t('ageDisclaimer.hospital-name')}</p>
                <p className="uppercase">
                    {t('ageDisclaimer.hospital-address')}
                </p>
                <div className="hospital-phones">
                    <p>{t('ageDisclaimer.hospital-phones')}</p>
                    <div className="phones">
                        <p>+381 113671429</p>
                        <p>+381 112662727</p>
                    </div>
                </div>
            </div>
            {showFinalWarning && (
                <div className="final-warning">
                    <p>{t('ageDisclaimer.finalWarning')}</p>
                </div>
            )}
        </>
    )
}

function CameraIcon() {
    return (
        <svg
            className="age-verification-card__cta-icon"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            <path
                d="M9.778 3c-.83 0-1.575.512-1.873 1.287l-.51 1.327H4.8A2.8 2.8 0 0 0 2 8.414v8.786A2.8 2.8 0 0 0 4.8 20h14.4a2.8 2.8 0 0 0 2.8-2.8V8.414a2.8 2.8 0 0 0-2.8-2.8h-2.595l-.51-1.327A2.007 2.007 0 0 0 14.222 3H9.778ZM12 17.1a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm0-2.1a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8Z"
                fill="currentColor"
            />
        </svg>
    )
}

function SpinningArrowsIcon() {
    return (
        <svg
            className="age-verification-scan__status-icon"
            width="24"
            height="24"
            viewBox="0 0 21.3333 21.3333"
            fill="none"
            aria-hidden="true">
            <path
                d="M0 21.3333V18.6667H3.66667L3.13333 18.2C1.97778 17.1778 1.16667 16.0111 0.7 14.7C0.233333 13.3889 0 12.0667 0 10.7333C0 8.26667 0.738889 6.07222 2.21667 4.15C3.69444 2.22778 5.62222 0.955556 8 0.333333V3.13333C6.4 3.71111 5.11111 4.69444 4.13333 6.08333C3.15556 7.47222 2.66667 9.02222 2.66667 10.7333C2.66667 11.7333 2.85556 12.7056 3.23333 13.65C3.61111 14.5944 4.2 15.4667 5 16.2667L5.33333 16.6V13.3333H8V21.3333H0V21.3333M13.3333 21V18.2C14.9333 17.6222 16.2222 16.6389 17.2 15.25C18.1778 13.8611 18.6667 12.3111 18.6667 10.6C18.6667 9.6 18.4778 8.62778 18.1 7.68333C17.7222 6.73889 17.1333 5.86667 16.3333 5.06667L16 4.73333V8H13.3333V0H21.3333V2.66667H17.6667L18.2 3.13333C19.2889 4.22222 20.0833 5.40556 20.5833 6.68333C21.0833 7.96111 21.3333 9.26667 21.3333 10.6C21.3333 13.0667 20.5944 15.2611 19.1167 17.1833C17.6389 19.1056 15.7111 20.3778 13.3333 21V21Z"
                fill="#B6C4FF"
            />
        </svg>
    )
}

function LockIcon() {
    return (
        <svg
            className="age-verification-scan__info-icon"
            width="44"
            height="57"
            viewBox="0 0 44 57"
            fill="none"
            aria-hidden="true">
            <path
                d="M5.5 57C3.9875 57 2.69271 56.4684 1.61563 55.4054C0.538542 54.3423 0 53.0643 0 51.5714V24.4286C0 22.9357 0.538542 21.6577 1.61563 20.5946C2.69271 19.5315 3.9875 19 5.5 19H8.25V13.5714C8.25 9.81667 9.59063 6.61607 12.2719 3.96964C14.9531 1.32321 18.1958 0 22 0C25.8042 0 29.0469 1.32321 31.7281 3.96964C34.4094 6.61607 35.75 9.81667 35.75 13.5714V19H38.5C40.0125 19 41.3073 19.5315 42.3844 20.5946C43.4615 21.6577 44 22.9357 44 24.4286V51.5714C44 53.0643 43.4615 54.3423 42.3844 55.4054C41.3073 56.4684 40.0125 57 38.5 57H5.5ZM22 43.4286C23.5125 43.4286 24.8073 42.897 25.8844 41.8339C26.9615 40.7708 27.5 39.4929 27.5 38C27.5 36.5071 26.9615 35.2292 25.8844 34.1661C24.8073 33.103 23.5125 32.5714 22 32.5714C20.4875 32.5714 19.1927 33.103 18.1156 34.1661C17.0385 35.2292 16.5 36.5071 16.5 38C16.5 39.4929 17.0385 40.7708 18.1156 41.8339C19.1927 42.897 20.4875 43.4286 22 43.4286ZM13.75 19H30.25V13.5714C30.25 11.3095 29.4479 9.3869 27.8438 7.80357C26.2396 6.22024 24.2917 5.42857 22 5.42857C19.7083 5.42857 17.7604 6.22024 16.1563 7.80357C14.5521 9.3869 13.75 11.3095 13.75 13.5714V19Z"
                fill="#0D3AA8"
            />
        </svg>
    )
}

function StaticAgeDisclaimer({
    navigate,
    nextRoute
}: {
    navigate: NavigateFunction
    nextRoute: string
}) {
    const { t } = useTranslate()

    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl={'/welcome-with-services'}
                navigationBackText={' '}
            />
            <div className="age-disclaimer-wrapper age-disclaimer-wrapper--static">
                <div className="age-disclaimer">
                    <AgeDisclaimerLegalContent />
                </div>
                <div className="action-wrapper">
                    <PrimaryButton
                        text={t('ageDisclaimer.continuePurchase')}
                        callback={() => navigate(nextRoute)}
                    />
                </div>
            </div>
            <Footer />
        </Container>
    )
}

type AgeVerificationPhase = 'info' | 'scanning' | AgeVerificationOutcome

// Production camera age-verification flow. Walks through: informational entry →
// live camera scan → adult / underage result or manual-confirmation fallback.
// Only definitive age results auto-advance.
function IcuCameraAgeVerification({
    navigate,
    nextRoute,
    recordingEnabled
}: {
    navigate: NavigateFunction
    nextRoute: string
    recordingEnabled: boolean
}) {
    const [phase, setPhase] = useState<AgeVerificationPhase>('info')
    const [rejectionMessage, setRejectionMessage] = useState<string>()
    const handleResult = useCallback(
        (result: AgeVerificationOutcome, message?: string) => {
            setRejectionMessage(message)
            setPhase(result)
        },
        []
    )

    if (phase === 'scanning') {
        return (
            <AgeVerificationScan
                onResult={handleResult}
                recordingEnabled={recordingEnabled}
            />
        )
    }

    if (phase === 'success') {
        return (
            <AgeVerificationResult
                variant="success"
                onComplete={() => navigate(nextRoute)}
            />
        )
    }

    if (phase === 'fail' || phase === 'spoof') {
        return (
            <AgeVerificationResult
                variant={phase}
                rejectionMessage={rejectionMessage}
                onComplete={() => navigate('/')}
            />
        )
    }

    if (phase === 'fallback') {
        return (
            <AgeVerificationFallback
                onConfirm={() => navigate(nextRoute)}
                onCancel={() => navigate('/welcome-with-services')}
            />
        )
    }

    return (
        <AgeVerificationInfo
            onConfirm={() => setPhase('scanning')}
            recordingEnabled={recordingEnabled}
        />
    )
}

// Informational entry screen: shows the legal disclaimer posters and explains
// that an automatic camera age check follows.
function AgeVerificationInfo({
    onConfirm,
    recordingEnabled
}: {
    onConfirm: () => void
    recordingEnabled: boolean
}) {
    const { t } = useTranslate()

    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl={'/welcome-with-services'}
                navigationBackText={' '}
            />
            <div className="age-disclaimer-wrapper age-disclaimer-wrapper--verification">
                <div className="age-verification-posters">
                    <img
                        className="age-verification-posters__image"
                        src={ageVerificationWarning}
                        alt=""
                    />
                    <img
                        className="age-verification-posters__image"
                        src={ageVerificationPoster}
                        alt=""
                    />
                </div>
                <div className="age-verification-card">
                    <p className="age-verification-card__title">
                        {t('ageVerification.title')}
                    </p>
                    <div className="age-verification-card__info">
                        <p>{t('ageVerification.cameraInfoLine1')}</p>
                        <p>
                            {t(
                                recordingEnabled
                                    ? 'ageVerification.recordingInfo'
                                    : 'ageVerification.cameraInfoLine2'
                            )}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="age-verification-card__cta"
                        onClick={onConfirm}>
                        <CameraIcon />
                        <span>{t('ageVerification.confirmButton')}</span>
                    </button>
                </div>
            </div>
            <Footer />
        </Container>
    )
}

// One verification request; the device service owns retries and detection policy.
function AgeVerificationScan({
    onResult,
    recordingEnabled
}: {
    onResult: (result: AgeVerificationOutcome, message?: string) => void
    recordingEnabled: boolean
}) {
    const { t } = useTranslate()
    const [streamUrl, setStreamUrl] = useState<string | null>(null)
    const [isScanning, setIsScanning] = useState(false)

    useEffect(() => {
        window.dispatchEvent(new Event('inactivity-pause'))

        return () => {
            window.dispatchEvent(new Event('inactivity-resume'))
        }
    }, [])

    useEffect(() => {
        let cancelled = false
        let releasePromise: Promise<void> | null = null
        let activationPromise: ReturnType<
            typeof apiService.activateICUCamera
        > | null = null
        const abortController = new AbortController()

        // Abort any in-flight verify-age, drop the FE stream consumer, then
        // deactivate the camera so ICU does not keep scanning after leave/back.
        const releaseCamera = () => {
            if (releasePromise) {
                return releasePromise
            }

            releasePromise = (async () => {
                if (!abortController.signal.aborted) {
                    abortController.abort()
                }
                if (!cancelled) {
                    setStreamUrl(null)
                    setIsScanning(false)
                }
                if (!activationPromise) return
                await activationPromise
                await wait(ICU_STREAM_RELEASE_SETTLE_MS)
                await apiService.deactivateICUCamera(
                    `icu-deactivate-${Date.now()}`
                )
            })()

            return releasePromise
        }

        const finish = (result: AgeVerificationOutcome, message?: string) => {
            void releaseCamera()
            if (!cancelled) {
                onResult(result, message)
            }
        }

        const runScan = async () => {
            activationPromise = apiService.activateICUCamera(
                `icu-activate-${Date.now()}`
            )
            const activation = await activationPromise
            if (cancelled) return
            if (activation.success && activation.data?.streamUrl) {
                setStreamUrl(
                    withIcuStreamCacheBust(
                        toAbsoluteIcuStreamUrl(activation.data.streamUrl)
                    )
                )
            }

            if (!activation.success) {
                finish('fallback')
                return
            }
            setIsScanning(true)
            const result = await apiService.verifyAgeWithICUCamera(
                undefined,
                `icu-verify-${Date.now()}`,
                abortController.signal
            )
            if (!cancelled) {
                finish(
                    getAgeVerificationOutcome(result),
                    typeof result.error?.message === 'string'
                        ? result.error.message
                        : undefined
                )
            }
        }

        // Defer until after React's StrictMode setup/cleanup replay.
        const startTimer = setTimeout(() => void runScan(), 0)

        return () => {
            cancelled = true
            clearTimeout(startTimer)
            void releaseCamera()
        }
    }, [onResult])

    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl={'/welcome-with-services'}
                navigationBackText={' '}
            />
            <div className="age-verification-scan">
                <div className="age-verification-scan__camera">
                    {streamUrl ? (
                        <IcuCameraStreamFeed
                            key={streamUrl}
                            streamUrl={streamUrl}
                        />
                    ) : (
                        <div className="age-verification-scan__feed-placeholder" />
                    )}
                    <div className="age-verification-scan__brackets">
                        <span className="age-verification-scan__bracket age-verification-scan__bracket--tl" />
                        <span className="age-verification-scan__bracket age-verification-scan__bracket--tr" />
                        <span className="age-verification-scan__bracket age-verification-scan__bracket--bl" />
                        <span className="age-verification-scan__bracket age-verification-scan__bracket--br" />
                    </div>
                    <div className="age-verification-scan__oval" />
                    {isScanning && (
                        <div className="age-verification-scan__status">
                            <SpinningArrowsIcon />
                            <span>
                                {t('ageVerification.analysisInProgress')}
                            </span>
                        </div>
                    )}
                </div>
                <div className="age-verification-scan__title-area">
                    <h1 className="age-verification-scan__title">
                        {t('ageVerification.scanTitle')}
                    </h1>
                    <p className="age-verification-scan__subtitle">
                        {t('ageVerification.scanSubtitle')}
                    </p>
                </div>
                <div className="age-verification-scan__cards">
                    <div className="age-verification-scan__info-card">
                        <LockIcon />
                        <div className="age-verification-scan__info-text">
                            <p className="age-verification-scan__info-title">
                                {t(
                                    recordingEnabled
                                        ? 'ageVerification.recordingTitle'
                                        : 'ageVerification.imageNotStoredTitle'
                                )}
                            </p>
                            <p>
                                {t(
                                    recordingEnabled
                                        ? 'ageVerification.recordingText'
                                        : 'ageVerification.imageNotStoredText'
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="age-verification-scan__info-card">
                        <img
                            className="age-verification-scan__device"
                            src={ageVerificationCameraAdjuster}
                            alt=""
                        />
                        <div className="age-verification-scan__info-text">
                            <p className="age-verification-scan__info-title">
                                {t('ageVerification.notInFrameTitle')}
                            </p>
                            <p>{t('ageVerification.notInFrameText')}</p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </Container>
    )
}

function AgeVerificationFallback({
    onConfirm,
    onCancel
}: {
    onConfirm: () => void
    onCancel: () => void
}) {
    const { t } = useTranslate()
    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl="/welcome-with-services"
                navigationBackText={t('header.back')}
            />
            <div className="age-verification-fallback">
                <div
                    className="age-verification-fallback__icon"
                    aria-hidden="true">
                    <svg viewBox="0 0 100 100" fill="none">
                        <path
                            d="M44 10a7 7 0 0 1 12 0l39 68a7 7 0 0 1-6 11H11a7 7 0 0 1-6-11Z"
                            fill="currentColor"
                        />
                        <path
                            d="M50 39v18"
                            stroke="#fff"
                            strokeWidth="10"
                            strokeLinecap="round"
                        />
                        <circle cx="50" cy="75" r="5" fill="#fff" />
                    </svg>
                </div>
                <h1>{t('ageVerification.fallbackTitle')}</h1>
                <div className="age-verification-fallback__message">
                    <p>{t('ageVerification.fallbackMessage')}</p>
                    <p>
                        <strong>
                            {t('ageVerification.fallbackConfirmation')}
                        </strong>
                    </p>
                </div>
                <div className="age-verification-fallback__actions">
                    <button type="button" onClick={onConfirm}>
                        {t('ageVerification.fallbackConfirmButton')}
                    </button>
                    <button
                        type="button"
                        className="age-verification-fallback__cancel"
                        onClick={onCancel}>
                        {t('ageVerification.fallbackCancelButton')}
                    </button>
                </div>
            </div>
            <Footer />
        </Container>
    )
}

function ResultStatusIcon({ variant }: { variant: 'success' | 'fail' }) {
    return (
        <svg
            className="age-verification-result__glyph"
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true">
            {variant === 'success' ? (
                <path
                    d="M20 6L9 17l-5-5"
                    stroke="#fff"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ) : (
                <path
                    d="M6 6l12 12M18 6L6 18"
                    stroke="#fff"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                />
            )}
        </svg>
    )
}

// Auto-advancing result screen for both the successful (18+) and rejected
// (underage or spoof) outcomes. After a short delay it triggers onComplete, which the
// flow uses to continue the voucher purchase or return home.
function AgeVerificationResult({
    variant,
    rejectionMessage,
    onComplete
}: {
    variant: 'success' | 'fail' | 'spoof'
    rejectionMessage?: string
    onComplete: () => void
}) {
    const { t } = useTranslate()
    const isSuccess = variant === 'success'
    const statusVariant = isSuccess ? 'success' : 'fail'

    useEffect(() => {
        const timeoutId = setTimeout(onComplete, AGE_RESULT_REDIRECT_MS)
        return () => clearTimeout(timeoutId)
    }, [onComplete])

    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl={'/welcome-with-services'}
                navigationBackText={' '}
            />
            <div
                className={`age-verification-result age-verification-result--${statusVariant}`}>
                <div className="age-verification-result__icon">
                    <div className="age-verification-result__icon-circle">
                        <ResultStatusIcon variant={statusVariant} />
                    </div>
                </div>
                <h1 className="age-verification-result__title">
                    {isSuccess
                        ? t('ageVerification.successTitle')
                        : t('ageVerification.failTitle')}
                </h1>
                <p className="age-verification-result__message">
                    {isSuccess
                        ? t('ageVerification.successMessageLine1')
                        : variant === 'spoof'
                          ? t('ageVerification.spoofMessage')
                          : t('ageVerification.failMessageLine1')}
                    <br />
                    {isSuccess
                        ? t('ageVerification.successMessageLine2')
                        : variant === 'spoof'
                          ? rejectionMessage || t('ageVerification.failMessageLine2')
                          : t('ageVerification.failMessageLine2')}
                </p>
                <div className="age-verification-result__redirect">
                    <span className="age-verification-result__spinner" />
                    <span className="age-verification-result__redirect-text">
                        {t('ageVerification.redirecting')}
                    </span>
                </div>
            </div>
            <Footer />
        </Container>
    )
}

function IcuCameraAgeVerificationDebug({
    navigate,
    nextRoute
}: {
    navigate: NavigateFunction
    nextRoute: string
}) {
    const [activeAction, setActiveAction] = useState<ActionType>(null)
    const [statusResponse, setStatusResponse] = useState('')
    const [activateResponse, setActivateResponse] = useState('')
    const [deactivateResponse, setDeactivateResponse] = useState('')
    const [visibleResponseType, setVisibleResponseType] = useState<
        Exclude<ActionType, 'detect'> | null
    >(null)
    const [cameraStreamUrl, setCameraStreamUrl] = useState<string | null>(null)
    const [isScanning, setIsScanning] = useState(false)
    const [latestDetectedAge, setLatestDetectedAge] = useState<number | null>(null)
    const [detectStatusText, setDetectStatusText] = useState(
        'Detection is idle'
    )
    const [detectIterations, setDetectIterations] = useState(0)

    const toPrettyJson = (payload: unknown): string =>
        JSON.stringify(payload, null, 2)

    const toAbsoluteStreamUrl = (streamUrl: string): string =>
        withIcuStreamCacheBust(toAbsoluteIcuStreamUrl(streamUrl))

    const handleStatus = async () => {
        const traceId = `icu-status-${Date.now()}`
        setVisibleResponseType('status')
        setActiveAction('status')
        try {
            const response = await apiService.getICUCameraStatus(traceId)
            console.log('ICU camera status response:', response)
            setStatusResponse(toPrettyJson(response))
            if (response.success && response.data?.streamUrl) {
                setCameraStreamUrl(toAbsoluteStreamUrl(response.data.streamUrl))
            } else {
                setCameraStreamUrl(null)
            }
        } catch (error) {
            setStatusResponse(
                toPrettyJson({
                    success: false,
                    message:
                        error instanceof Error ? error.message : 'Unknown error'
                })
            )
        } finally {
            setActiveAction(null)
        }
    }

    const handleActivate = async () => {
        const traceId = `icu-activate-${Date.now()}`
        setVisibleResponseType('activate')
        setActiveAction('activate')
        try {
            // Release any existing FE consumer before opening a new stream.
            setCameraStreamUrl(null)
            await wait(ICU_STREAM_RELEASE_SETTLE_MS)

            const response = await apiService.activateICUCamera(traceId)
            console.log('ICU camera activate response:', response)
            setActivateResponse(toPrettyJson(response))
            if (response.success && response.data?.streamUrl) {
                setCameraStreamUrl(toAbsoluteStreamUrl(response.data.streamUrl))
            }
        } catch (error) {
            setActivateResponse(
                toPrettyJson({
                    success: false,
                    message:
                        error instanceof Error ? error.message : 'Unknown error'
                })
            )
        } finally {
            setActiveAction(null)
        }
    }

    const handleDeactivate = async () => {
        const traceId = `icu-deactivate-${Date.now()}`
        setVisibleResponseType('deactivate')
        setActiveAction('deactivate')
        try {
            // Clear the feed first so the browser aborts MJPEG before deactivate.
            setCameraStreamUrl(null)
            await wait(ICU_STREAM_RELEASE_SETTLE_MS)

            const response = await apiService.deactivateICUCamera(traceId)
            console.log('ICU camera deactivate response:', response)
            setDeactivateResponse(toPrettyJson(response))
            setIsScanning(false)
            setDetectStatusText('Detection stopped (camera deactivated)')
        } catch (error) {
            setDeactivateResponse(
                toPrettyJson({
                    success: false,
                    message:
                        error instanceof Error ? error.message : 'Unknown error'
                })
            )
        } finally {
            setActiveAction(null)
        }
    }

    const handleDetect = async () => {
        setVisibleResponseType(null)
        setIsScanning(true)
        setDetectStatusText('Scanning...')
        try {
            const response = await apiService.verifyAgeWithICUCamera(
                undefined,
                `icu-detect-${Date.now()}`
            )
            setDetectIterations((prev) => prev + 1)
            setLatestDetectedAge(response.data?.age ?? null)
            setDetectStatusText(
                `Scan completed: ${getAgeVerificationOutcome(response)}`
            )
        } finally {
            setIsScanning(false)
        }
    }

    const handleContinueWithoutVerification = () => {
        navigate(nextRoute)
    }

    return (
        <Container isFullHeight={true}>
            <Header navigateBackUrl={'/welcome-with-services'} navigationBackText={' '} />
            <div className="age-disclaimer-wrapper">
                <div className="icu-test-page">
                    <h1>ICU Camera Test Page</h1>
                    <div className="icu-skip-flow">
                        <p className="icu-skip-flow__hint">
                            Skip ICU age check and continue the bet voucher flow
                            (same as after successful verification).
                        </p>
                        <PrimaryButton
                            text="Continue to betting (skip verification)"
                            callback={handleContinueWithoutVerification}
                        />
                    </div>
                    <div className="icu-stream-block">
                        <h2>Camera Stream</h2>
                        {cameraStreamUrl && (
                            <p className="icu-stream-url">{cameraStreamUrl}</p>
                        )}
                        {cameraStreamUrl ? (
                            <IcuCameraStreamFeed
                                key={cameraStreamUrl}
                                streamUrl={cameraStreamUrl}
                                className="icu-stream"
                            />
                        ) : (
                            <div className="icu-stream-placeholder">
                                Press Check Status or Activate Camera to display stream
                            </div>
                        )}
                    </div>

                    <div className="icu-action-block">
                        <PrimaryButton
                            text={
                                activeAction === 'status'
                                    ? 'Checking status...'
                                    : 'Check Status'
                            }
                            callback={handleStatus}
                            isDisabled={activeAction !== null}
                        />
                        {visibleResponseType === 'status' && statusResponse && (
                            <pre className="icu-response">{statusResponse}</pre>
                        )}
                    </div>

                    <div className="icu-action-block">
                        <PrimaryButton
                            text={
                                activeAction === 'activate'
                                    ? 'Activating camera...'
                                    : 'Activate Camera'
                            }
                            callback={handleActivate}
                            isDisabled={activeAction !== null}
                        />
                        {visibleResponseType === 'activate' &&
                            activateResponse && (
                            <pre className="icu-response">{activateResponse}</pre>
                        )}
                    </div>

                    <div className="icu-action-block">
                        <PrimaryButton
                            text={
                                activeAction === 'deactivate'
                                    ? 'Stopping camera...'
                                    : 'Stop Camera'
                            }
                            callback={handleDeactivate}
                            isDisabled={activeAction !== null}
                        />
                        {visibleResponseType === 'deactivate' &&
                            deactivateResponse && (
                            <pre className="icu-response">{deactivateResponse}</pre>
                        )}
                    </div>

                    <div className="icu-action-block">
                        <PrimaryButton
                            text={isScanning ? 'Scanning...' : 'Begin Detection'}
                            callback={handleDetect}
                            isDisabled={
                                isScanning || activeAction === 'status' ||
                                activeAction === 'activate' ||
                                activeAction === 'deactivate'
                            }
                        />
                        <p className="detect-status">
                            {isScanning ? 'Scanning...' : detectStatusText}
                        </p>
                        <div className="detect-age-block">
                            <p className="detect-age-label">Detected age</p>
                            <p className="detect-age-value">
                                {latestDetectedAge ?? '--'}
                            </p>
                            <p className="detect-age-meta">
                                Scans completed: {detectIterations}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </Container>
    )
}
